import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import applyChartDefaults from "./applyChartDefaults"

export const THEMES = {
  LIGHT: "light",
  DARK: "dark"
}

export const THEME_STORAGE_KEY = "avdhaan-theme"

/**
 * Light is the product default and stays the default: an unset or unreadable preference resolves
 * to light, and the OS `prefers-color-scheme` is deliberately NOT consulted — a user who has never
 * touched the switch must keep seeing exactly the theme they see today.
 */
export const readStoredTheme = () => {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === THEMES.DARK ? THEMES.DARK : THEMES.LIGHT
  } catch (error) {
    return THEMES.LIGHT
  }
}

/**
 * Dark mode is expressed as `data-theme="dark"` on <html>, and light mode as the *absence* of the
 * attribute. Nothing in the dark stylesheet can then match while light is active, which is what
 * keeps the existing theme provably untouched.
 */
export const applyThemeToDocument = (theme) => {
  const root = document.documentElement
  if (theme === THEMES.DARK) {
    root.setAttribute("data-theme", THEMES.DARK)
  } else {
    root.removeAttribute("data-theme")
  }
}

const ThemeContext = createContext({
  theme: THEMES.LIGHT,
  isDark: false,
  setTheme: () => {},
  toggleTheme: () => {}
})

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readStoredTheme)

  useEffect(() => {
    applyThemeToDocument(theme)
    // Chart.js reads its palette from a global, so it is set alongside the DOM attribute rather
    // than per chart. Charts repaint because every chart component subscribes to this context.
    applyChartDefaults(theme === THEMES.DARK)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme)
    } catch (error) {
      // A blocked storage (private mode, cleared site data) costs persistence across reloads,
      // not the theme itself — so there is nothing to recover from here.
    }
  }, [theme])

  // Keep tabs of the same app in sync when the preference changes in another one.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === THEME_STORAGE_KEY) {
        setThemeState(event.newValue === THEMES.DARK ? THEMES.DARK : THEMES.LIGHT)
      }
    }
    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [])

  const setTheme = useCallback((next) => {
    setThemeState(next === THEMES.DARK ? THEMES.DARK : THEMES.LIGHT)
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeState((current) => (current === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK))
  }, [])

  const value = useMemo(
    () => ({ theme, isDark: theme === THEMES.DARK, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = () => useContext(ThemeContext)

export default ThemeProvider
